import { api, del, get, patch, post, put } from '../client'
import type {
  CandidateCertification,
  CandidateEducation,
  CandidateExperience,
  CandidateOverviewResponse,
  CandidateProfileHub,
  CandidateSignal,
  CandidateSkillEntry,
  CertificationProofUrl,
  CreateCertificationRequest,
  CreateEducationRequest,
  CreateExperienceRequest,
  CreateLocationSignalRequest,
  GenerateLocationSummaryRequest,
  GenerateOverviewRequest,
  ImportFromCVResponse,
  LocationPreferenceResponse,
  LocationSummaryGenerationResponse,
  UpdateCertificationRequest,
  UpdateEducationRequest,
  UpdateExperienceRequest,
} from '../types/generated'

export async function getProfileHub(): Promise<CandidateProfileHub> {
  return get<CandidateProfileHub>('api/candidate/profile-hub')
}

export async function createExperience(req: CreateExperienceRequest): Promise<CandidateExperience> {
  return post<CandidateExperience>('api/candidate/profile-hub/experiences', req)
}

export async function updateExperience(
  id: number,
  req: UpdateExperienceRequest
): Promise<CandidateExperience> {
  return put<CandidateExperience>(`api/candidate/profile-hub/experiences/${id}`, req)
}

export async function deleteExperience(id: number): Promise<void> {
  await del(`api/candidate/profile-hub/experiences/${id}`)
}

export async function createEducation(req: CreateEducationRequest): Promise<CandidateEducation> {
  return post<CandidateEducation>('api/candidate/profile-hub/education', req)
}

export async function updateEducation(
  id: number,
  req: UpdateEducationRequest
): Promise<CandidateEducation> {
  return put<CandidateEducation>(`api/candidate/profile-hub/education/${id}`, req)
}

export async function deleteEducation(id: number): Promise<void> {
  await del(`api/candidate/profile-hub/education/${id}`)
}

export async function createCertification(
  req: CreateCertificationRequest
): Promise<CandidateCertification> {
  return post<CandidateCertification>('api/candidate/profile-hub/certifications', req)
}

export async function updateCertification(
  id: number,
  req: UpdateCertificationRequest
): Promise<CandidateCertification> {
  return put<CandidateCertification>(`api/candidate/profile-hub/certifications/${id}`, req)
}

export async function deleteCertification(id: number): Promise<void> {
  await del(`api/candidate/profile-hub/certifications/${id}`)
}

export async function uploadCertificationProof(
  id: number,
  file: File
): Promise<CandidateCertification> {
  const formData = new FormData()
  formData.append('proof', file)
  return api
    .post(`api/candidate/profile-hub/certifications/${id}/proof`, { body: formData })
    .json<CandidateCertification>()
}

export async function getCertificationProofUrl(id: number): Promise<CertificationProofUrl> {
  return get<CertificationProofUrl>(`api/candidate/profile-hub/certifications/${id}/proof`)
}

export async function addSkill(skillName: string): Promise<CandidateSkillEntry> {
  return post<CandidateSkillEntry>('api/candidate/profile-hub/skills', { skill_name: skillName })
}

export async function removeSkill(id: number): Promise<void> {
  await del(`api/candidate/profile-hub/skills/${id}`)
}

export async function importFromCV(): Promise<ImportFromCVResponse> {
  return post<ImportFromCVResponse>('api/candidate/profile-hub/import-cv', {})
}

export async function generateOverview(
  req: GenerateOverviewRequest = {}
): Promise<CandidateOverviewResponse> {
  return post<CandidateOverviewResponse>('api/candidate/profile-hub/overview', req)
}

export async function getLocationPreferences(): Promise<LocationPreferenceResponse> {
  return get<LocationPreferenceResponse>('api/candidate/profile-hub/location-signals')
}

export async function addLocationSignal(
  req: CreateLocationSignalRequest
): Promise<CandidateSignal> {
  return post<CandidateSignal>('api/candidate/profile-hub/location-signals', req)
}

export async function removeLocationSignal(id: number): Promise<void> {
  await del(`api/candidate/profile-hub/location-signals/${id}`)
}

export async function updateLocationSignalTier(id: number, tier: string): Promise<void> {
  await patch(`api/candidate/profile-hub/location-signals/${id}`, { tier })
}

export async function updateLocationSummary(summary: string): Promise<void> {
  await put('api/candidate/profile-hub/location-summary', { summary })
}

export async function generateLocationSummary(
  req: GenerateLocationSummaryRequest = {}
): Promise<LocationSummaryGenerationResponse> {
  return post<LocationSummaryGenerationResponse>(
    'api/candidate/profile-hub/generate-location-summary',
    req
  )
}
